import { notificationController } from "@/utils/notificationController";
import { App } from "antd";

// Function to generate code from name
export const generateCodeFromName = (name: string): string => {
  if (!name) return "";

  // Remove special characters and convert to uppercase
  const cleanName = name
    .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special characters except spaces
    .replace(/\s+/g, "") // Remove all spaces
    .toUpperCase(); // Convert to uppercase

  return cleanName;
};

// Hook to get the App instance for notifications
export const useAppNotification = () => {
  const { notification } = App.useApp();
  return notification;
};

// Common notification types
export type NotificationType = "success" | "error" | "info" | "warning";

// Notification configuration interface
export interface NotificationConfig {
  type: NotificationType;
  message: string;
  description?: string;
  placement?: "topRight" | "topLeft" | "bottomRight" | "bottomLeft";
  duration?: number;
}

// Default notification configuration
const DEFAULT_NOTIFICATION_CONFIG = {
  placement: "topRight" as const,
  duration: 4.5,
};

// Common operation types for standardized messages
export type OperationType =
  | "create"
  | "update"
  | "delete"
  | "fetch"
  | "save"
  | "upload"
  | "download"
  | "import"
  | "export"
  | "login"
  | "logout"
  | "register"
  | "reset"
  | "verify"
  | "approve"
  | "reject"
  | "activate"
  | "deactivate";

// Entity types for standardized messages
export type EntityType =
  | "partner"
  | "facility"
  | "user"
  | "program"
  | "file"
  | "data"
  | "record"
  | "item"
  | "account"
  | "profile"
  | "settings"
  | "configuration"
  | "password";

// Standardized notification messages
const getStandardMessage = (
  operation: OperationType,
  entity: EntityType,
  isSuccess: boolean
): { message: string; description: string } => {
  const entityPlural = entity === "facility" ? "facilities" : `${entity}s`;
  const entityCapitalized = entity.charAt(0).toUpperCase() + entity.slice(1);
  const entityPluralCapitalized =
    entityPlural.charAt(0).toUpperCase() + entityPlural.slice(1);

  if (isSuccess) {
    const messages = {
      create: {
        message: "Success",
        description: `${entityCapitalized} created successfully`,
      },
      update: {
        message: "Success",
        description: `${entityCapitalized} updated successfully`,
      },
      delete: {
        message: "Success",
        description: `${entityCapitalized} deleted successfully`,
      },
      fetch: {
        message: "Success",
        description: `${entityPluralCapitalized} loaded successfully`,
      },
      save: { message: "Success", description: "Changes saved successfully" },
      upload: { message: "Success", description: "File uploaded successfully" },
      download: {
        message: "Success",
        description: "File downloaded successfully",
      },
      import: { message: "Success", description: "Data imported successfully" },
      export: { message: "Success", description: "Data exported successfully" },
      login: { message: "Success", description: "Logged in successfully" },
      logout: { message: "Success", description: "Logged out successfully" },
      register: { message: "Success", description: "Registration successful" },
      reset: { message: "Success", description: "Password reset successfully" },
      verify: { message: "Success", description: "Verification successful" },
      approve: { message: "Success", description: "Approved successfully" },
      reject: { message: "Success", description: "Rejected successfully" },
      activate: { message: "Success", description: "Activated successfully" },
      deactivate: {
        message: "Success",
        description: "Deactivated successfully",
      },
    };
    return messages[operation];
  } else {
    const messages = {
      create: { message: "Error", description: `Failed to create ${entity}` },
      update: { message: "Error", description: `Failed to update ${entity}` },
      delete: { message: "Error", description: `Failed to delete ${entity}` },
      fetch: {
        message: "Error",
        description: `Failed to load ${entityPlural}`,
      },
      save: { message: "Error", description: "Failed to save changes" },
      upload: { message: "Error", description: "Failed to upload file" },
      download: { message: "Error", description: "Failed to download file" },
      import: { message: "Error", description: "Failed to import data" },
      export: { message: "Error", description: "Failed to export data" },
      login: { message: "Error", description: "Login failed" },
      logout: { message: "Error", description: "Logout failed" },
      register: { message: "Error", description: "Registration failed" },
      reset: { message: "Error", description: "Password reset failed" },
      verify: { message: "Error", description: "Verification failed" },
      approve: { message: "Error", description: "Approval failed" },
      reject: { message: "Error", description: "Rejection failed" },
      activate: { message: "Error", description: "Activation failed" },
      deactivate: { message: "Error", description: "Deactivation failed" },
    };
    return messages[operation];
  }
};

/**
 * Show a notification with the specified configuration using App component
 * @param config - Notification configuration
 */
export const showNotification = (config: NotificationConfig): void => {
  const { type, message, description, placement, duration } = {
    ...DEFAULT_NOTIFICATION_CONFIG,
    ...config,
  };

  notificationController[type]({
    message,
    description,
    placement,
    duration,
  });
};

/**
 * Show a success notification using App component
 * @param message - Success message
 * @param description - Optional description
 * @param placement - Optional placement (default: topRight)
 */
export const showSuccessNotification = (
  message: string = "Success",
  description?: string,
  placement: "topRight" | "topLeft" | "bottomRight" | "bottomLeft" = "topRight"
): void => {
  showNotification({
    type: "success",
    message,
    description,
    placement,
  });
};

/**
 * Show an error notification using App component
 * @param message - Error message
 * @param description - Optional description
 * @param placement - Optional placement (default: topRight)
 */
export const showErrorNotification = (
  message: string = "Error",
  description?: string,
  placement: "topRight" | "topLeft" | "bottomRight" | "bottomLeft" = "topRight"
): void => {
  showNotification({
    type: "error",
    message,
    description,
    placement,
  });
};

/**
 * Show an info notification using App component
 * @param message - Info message
 * @param description - Optional description
 * @param placement - Optional placement (default: topRight)
 */
export const showInfoNotification = (
  message: string = "Info",
  description?: string,
  placement: "topRight" | "topLeft" | "bottomRight" | "bottomLeft" = "topRight"
): void => {
  showNotification({
    type: "info",
    message,
    description,
    placement,
  });
};

/**
 * Show a warning notification using App component
 * @param message - Warning message
 * @param description - Optional description
 * @param placement - Optional placement (default: topRight)
 */
export const showWarningNotification = (
  message: string = "Warning",
  description?: string,
  placement: "topRight" | "topLeft" | "bottomRight" | "bottomLeft" = "topRight"
): void => {
  showNotification({
    type: "warning",
    message,
    description,
    placement,
  });
};

/**
 * Show a standardized success notification for common operations using App component
 * @param operation - The operation performed (create, update, delete, etc.)
 * @param entity - The entity type (partner, facility, user, etc.)
 * @param customDescription - Optional custom description to override default
 * @param placement - Optional placement (default: topRight)
 */
export const showOperationSuccessNotification = (
  operation: OperationType,
  entity: EntityType,
  customDescription?: string,
  placement: "topRight" | "topLeft" | "bottomRight" | "bottomLeft" = "topRight"
): void => {
  const { message, description } = getStandardMessage(operation, entity, true);
  showSuccessNotification(message, customDescription || description, placement);
};

/**
 * Show a standardized error notification for common operations using App component
 * @param operation - The operation that failed (create, update, delete, etc.)
 * @param entity - The entity type (partner, facility, user, etc.)
 * @param customDescription - Optional custom description to override default
 * @param placement - Optional placement (default: topRight)
 */
export const showOperationErrorNotification = (
  operation: OperationType,
  entity: EntityType,
  customDescription?: string,
  placement: "topRight" | "topLeft" | "bottomRight" | "bottomLeft" = "topRight"
): void => {
  const { message, description } = getStandardMessage(operation, entity, false);
  showErrorNotification(message, customDescription || description, placement);
};

/**
 * Handle API operation with automatic success/error notifications using App component
 * @param operation - The operation being performed
 * @param entity - The entity type
 * @param apiCall - The async function to execute
 * @param successCallback - Optional callback to run on success
 * @param errorCallback - Optional callback to run on error
 * @param customSuccessMessage - Optional custom success message
 * @param customErrorMessage - Optional custom error message
 */
export const handleApiOperation = async <T>(
  operation: OperationType,
  entity: EntityType,
  apiCall: () => Promise<T>,
  successCallback?: (result: T) => void,
  errorCallback?: (error: any) => void,
  customSuccessMessage?: string,
  customErrorMessage?: string
): Promise<T | null> => {
  try {
    const result = await apiCall();
    showOperationSuccessNotification(operation, entity, customSuccessMessage);
    if (successCallback) {
      successCallback(result);
    }
    return result;
  } catch (error) {
    showOperationErrorNotification(operation, entity, customErrorMessage);
    if (errorCallback) {
      errorCallback(error);
    }
    return null;
  }
};

// Utility function to get the first available page based on user permissions
export const getFirstAvailablePage = (user: any): string => {
  if (!user?.feature_access) {
    return "/dashboard"; // Default fallback
  }

  const availablePages: string[] = [];

  // Check each feature access permission
  user.feature_access.forEach((fa: any) => {
    const { feature, can_read } = fa;

    if (!can_read) return;

    switch (feature.name) {
      case "Dashboard":
        availablePages.push("/dashboard");
        break;
      case "Master Report":
        availablePages.push("/master-report");
        break;
      case "Budget Pacing":
        availablePages.push("/budget-pacing");
        break;
      case "Users":
        availablePages.push("/admin/users");
        break;
      case "Roles":
        availablePages.push("/admin/roles");
        break;
      case "Row Level Settings":
        availablePages.push("/admin/row-level-settings");
        break;
    }
  });

  // Return the first available page, or dashboard as fallback
  return availablePages.length > 0 ? availablePages[0] : "/dashboard";
};

// Utility function for login redirects - prioritizes dashboard access
export const getLoginRedirectPage = (user: any): string => {
    // Force non-admins with temp password to change password on first login
    if (user?.temp_password_expires_at) {
      // Redirect to profile page, focusing the change password tab
      return "/profile#change-password";
    }

  if (!user?.feature_access) {
    return "/dashboard"; // Default fallback
  }

  // Check if user has dashboard access first
  const hasDashboardAccess = user.feature_access.some(
    (fa: any) => fa.feature.name === "Dashboard" && fa.can_read
  );

  if (hasDashboardAccess) {
    return "/dashboard";
  }

  // If no dashboard access, get the first available page
  return getFirstAvailablePage(user);
};
